import { useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ViewToggle } from '@/components/ledgers/ViewToggle';
import { LedgersListView } from '@/components/ledgers/LedgersListView';
import { LedgersAnalyticsView } from '@/components/ledgers/LedgersAnalyticsView';
import { PageHeader } from '@/components/ui/PageHeader';
import { usePageTitle } from '@/hooks/usePageTitle';
import type { LedgerType } from '@/api/types';

interface LedgersPageProps {
    type: LedgerType;
}

/**
 * Shared page component for Assets and Debts pages.
 * Acts as a shell that switches between list and analytics views based on URL.
 */
export function LedgersPage({ type }: LedgersPageProps) {
    const [searchParams] = useSearchParams();
    const view = searchParams.get('view') === 'analytics' ? 'analytics' : 'list';
    const typeLabelPlural = type === 'asset' ? 'Assets' : 'Debts';
    const [headerAction, setHeaderAction] = useState<ReactNode>(null);

    usePageTitle(typeLabelPlural);

    return (
        <div className="space-y-6">
            {/* Header */}
            <PageHeader title={typeLabelPlural}>
                <div className="flex items-center gap-2">
                    <ViewToggle currentView={view} />
                    {headerAction}
                </div>
            </PageHeader>

            {/* View content */}
            {view === 'analytics' ? (
                <LedgersAnalyticsView type={type} onHeaderAction={setHeaderAction} />
            ) : (
                <LedgersListView type={type} onHeaderAction={setHeaderAction} />
            )}
        </div>
    );
}
